import { useState, useRef, useEffect, useCallback } from "react";
import ChatAttachmentComposer from "@/pages/chat/components/ChatAttachmentComposer";
import { ChatAttachmentComposerController } from "@/pages/chat/components/ChatAttachmentComposerController";
import { attachmentApi } from "@/api/AttachmentApiClient";
import MessageAttachments from "@/components/chat/MessageAttachments";
import AgentMessageAvatar from "@/components/chat/AgentMessageAvatar";
import ChatMessageBody from "@/components/chat/ChatMessageBody";
import ComposerLinkControl from "@/components/chat/ComposerLinkControl";
import { isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";

import { fetchTicketEmailSenders } from "@/api/ticketEmailApi";
import { OpenedChatScrollController } from "@/lib/OpenedChatScrollController";

const TICKET_STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  on_hold: "On Hold",
  resolved: "Resolved",
};

function ticketStatusLabel(status) {
  return TICKET_STATUS_LABELS[status] ?? String(status ?? "");
}

function formatSenderLabel(sender) {
  const name = (sender?.name || "").trim();
  const email = (sender?.email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return email || name || "Sender";
}

function pickDefaultSenderEmail(senders, messages, ticket) {
  if (!Array.isArray(senders) || !senders.length) return "";
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.sender !== "agent") continue;
    const prior = (msg.senderEmail || msg.sender_email || "").trim().toLowerCase();
    if (!prior) continue;
    const match = senders.find((s) => (s.email || "").trim().toLowerCase() === prior);
    if (match) return match.email;
  }
  const fallback = senders.find((s) => s.isDefault) || senders[0];
  return fallback?.email || "";
}

export default function TicketingChatThread({
  conversationId,
  conversation,
  messages,
  threadLoading,
  threadError,
  sendMessage,
  onRetryThread,
  selectedTicket,
  submitTicketWithStatus,
  onAfterTicketAction,
  workspaceId = null,
}) {
  const conv = conversation;
  const [replyText, setReplyText] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [ticketSubmitBusy, setTicketSubmitBusy] = useState(false);
  const [sendActionsOpen, setSendActionsOpen] = useState(false);
  const [emailSenders, setEmailSenders] = useState([]);
  const [emailProvider, setEmailProvider] = useState("smtp");
  const [selectedFromEmail, setSelectedFromEmail] = useState("");
  const [showNewMessageChip, setShowNewMessageChip] = useState(false);
  const bottomRef = useRef(null);
  const threadScrollRef = useRef(null);
  const scrollControllerRef = useRef(null);
  if (!scrollControllerRef.current) {
    scrollControllerRef.current = new OpenedChatScrollController();
  }
  const textareaRef = useRef(null);
  const sendActionsRef = useRef(null);
  const attachControllerRef = useRef(null);
  if (!attachControllerRef.current) {
    attachControllerRef.current = new ChatAttachmentComposerController();
  }
  const attachController = attachControllerRef.current;
  const isEmailTicket = selectedTicket?.source === "email";
  // Zendesk owns outbound delivery — From picker is SMTP multi-sender only.
  const showFromPicker = isEmailTicket && emailProvider !== "zendesk" && emailSenders.length > 0;
  const selectedSender =
    emailSenders.find((s) => (s.email || "").trim() === selectedFromEmail) ||
    emailSenders.find((s) => s.isDefault) ||
    emailSenders[0] ||
    null;

  useEffect(() => {
    if (!workspaceId) return undefined;
    let live = true;
    void fetchTicketEmailSenders({ workspaceId })
      .then((payload) => {
        if (!live) return;
        const rows = Array.isArray(payload?.senders) ? payload.senders : [];
        setEmailSenders(rows);
        setEmailProvider(String(payload?.provider || "smtp").trim().toLowerCase() || "smtp");
      })
      .catch(() => {
        if (live) {
          setEmailSenders([]);
          setEmailProvider("smtp");
        }
      });
    return () => {
      live = false;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!showFromPicker) {
      setSelectedFromEmail("");
      return;
    }
    setSelectedFromEmail(pickDefaultSenderEmail(emailSenders, messages, selectedTicket));
  }, [selectedTicket?.id, showFromPicker, emailSenders, messages]);

  const fetchDownloadUrl = useCallback(
    (attachmentId) =>
      attachmentApi.fetchDownloadUrl({
        attachmentId,
        workspaceId,
        sessionId: conversationId || undefined,
        context: selectedTicket?.id ? "ticket" : "live_chat",
      }),
    [workspaceId, conversationId, selectedTicket?.id],
  );

  useEffect(() => {
    setReplyText("");
    setSendActionsOpen(false);
    attachController.setContext(
      selectedTicket?.id ? "ticket" : "live_chat",
      selectedTicket?.id ?? null,
    );
  }, [conversationId, selectedTicket?.id, attachController]);

  useEffect(() => {
    const ctrl = scrollControllerRef.current;
    if (!ctrl) return;
    const decision = ctrl.evaluate(conversationId, messages.length);
    if (decision.behavior) {
      bottomRef.current?.scrollIntoView({ behavior: decision.behavior });
    }
    setShowNewMessageChip(decision.showChip);
  }, [conversationId, messages.length]);

  useEffect(() => {
    if (!sendActionsOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!sendActionsRef.current?.contains(event.target)) setSendActionsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setSendActionsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [sendActionsOpen]);

  const uploadPendingAttachments = async () => {
    if (!isHumanAgentInboxLive()) return [];
    if (selectedTicket?.id) {
      attachController.setContext("ticket", selectedTicket.id);
      return attachController.uploadAllPending(attachmentApi, {
        sessionId: conversationId || "",
        workspaceId,
      });
    }
    if (conversationId) {
      attachController.setContext("live_chat");
      return attachController.uploadAllPending(attachmentApi, {
        sessionId: conversationId,
        workspaceId,
      });
    }
    return [];
  };

  const handleSend = async () => {
    if (!attachController.canSend(replyText) || sendBusy || ticketSubmitBusy) return;
    setSendBusy(true);
    try {
      const attachmentIds = await uploadPendingAttachments();
      const meta = attachController.metadataForOptimistic();
      // Always persist ticket replies on the ticket (not the closed live-chat session).
      // Email source → reply-email / Zendesk; chat source → /messages (Zendesk sync on backend).
      if (selectedTicket && submitTicketWithStatus) {
        await submitTicketWithStatus({
          ticketId: selectedTicket.id,
          conversationId,
          messageText: replyText,
          senderTab: "reply",
          newStatus: selectedTicket.status,
          attachmentIds,
          source: selectedTicket.source || (isEmailTicket ? "email" : undefined),
          requesterEmail: selectedTicket.requesterEmail,
          fromEmail: selectedSender?.email,
          fromName: selectedSender?.name,
          subject: selectedTicket.subject,
        });
        onAfterTicketAction?.();
      } else if (sendMessage) {
        await sendMessage(replyText, "reply", {
          attachmentIds,
          attachments: meta.length ? meta : undefined,
        });
      }
      attachController.clear();
      setReplyText("");
      setSendActionsOpen(false);
    } catch (e) {
      surfaceInboxHttpError(e);
    } finally {
      setSendBusy(false);
    }
  };

  const handleSubmitTicketAs = useCallback(
    async (newStatus) => {
      if (!selectedTicket || !submitTicketWithStatus || ticketSubmitBusy) return;
      setTicketSubmitBusy(true);
      try {
        let attachmentIds = [];
        if (isHumanAgentInboxLive()) {
          attachController.setContext("ticket", selectedTicket.id);
          attachmentIds = await attachController.uploadAllPending(attachmentApi, {
            sessionId: conversationId || "",
            workspaceId,
          });
        }
        await submitTicketWithStatus({
          ticketId: selectedTicket.id,
          conversationId,
          messageText: replyText,
          senderTab: "reply",
          newStatus,
          attachmentIds,
          source: selectedTicket.source || (isEmailTicket ? "email" : undefined),
          requesterEmail: selectedTicket.requesterEmail,
          fromEmail: selectedSender?.email,
          fromName: selectedSender?.name,
          subject: selectedTicket.subject,
        });
        attachController.clear();
        setReplyText("");
        setSendActionsOpen(false);
        onAfterTicketAction?.();
      } catch (e) {
        surfaceInboxHttpError(e);
      } finally {
        setTicketSubmitBusy(false);
      }
    },
    [
      selectedTicket,
      submitTicketWithStatus,
      ticketSubmitBusy,
      conversationId,
      replyText,
      onAfterTicketAction,
      attachController,
      isEmailTicket,
      selectedSender,
      workspaceId,
    ],
  );

  if (threadLoading && !conv) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200" />
        <p className="text-sm font-medium text-slate-500">Loading conversation…</p>
      </div>
    );
  }

  if (threadError && !conv) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <p className="text-sm text-rose-700">{threadError.message}</p>
        {onRetryThread && (
          <button type="button" className="ui-btn-primary !py-2 !text-xs" onClick={() => onRetryThread()}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!conv && !selectedTicket) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-white px-6 text-center">
        <p className="text-sm font-medium text-slate-500">Select a ticket from the queue.</p>
      </div>
    );
  }

  return (
    <div className="ticketing-thread-root">
      <header className="ticketing-thread-header">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            {conv?.identityEmails?.[0] ??
              selectedTicket?.requesterEmail ??
              (conv?.workspaceName ? `User from ${conv.workspaceName}` : "Visitor")}
          </h3>
        </div>
      </header>

      {selectedTicket && (
        <div className="ticketing-thread-banner">
          <p className="text-xs font-semibold text-teal-950">
            <i className="ri-ticket-2-line mr-1.5 align-text-bottom text-teal-600" aria-hidden />
            Ticket <span className="font-mono">#{selectedTicket.number ?? selectedTicket.id}</span>
            <span className="font-normal text-teal-700"> · {ticketStatusLabel(selectedTicket.status)}</span>
          </p>
        </div>
      )}

      <div
        ref={threadScrollRef}
        className="ticketing-thread-messages"
        onScroll={() => {
          const ctrl = scrollControllerRef.current;
          if (!ctrl) return;
          ctrl.recordScrollPosition(threadScrollRef.current);
          setShowNewMessageChip(ctrl.showChip);
        }}
      >
        {messages.map((msg) => {
          if (msg.type === "system") {
            return (
              <div key={msg.id} className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-200" />
                <div className="flex max-w-[90%] items-center gap-2 rounded-full bg-slate-100/90 px-3 py-1.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200/60">
                  <i className="ri-information-line text-sm text-teal-500" aria-hidden />
                  <span className="leading-snug">{msg.text}</span>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            );
          }
          if (msg.sender === "user") {
            const hideUserText = msg.attachments?.length && (msg.text || "").trim() === "[Attachment]";
            return (
              <div key={msg.id} className="flex items-end gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 ring-2 ring-white shadow-md">
                  <i className="ri-user-3-line text-sm text-slate-500" aria-hidden />
                </div>
                <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                  {!hideUserText ? (
                    <div className="chat-message-bubble rounded-2xl rounded-bl-md border border-slate-200/80 bg-white px-4 py-3 text-sm leading-relaxed text-slate-800 shadow-sm">
                      <ChatMessageBody text={msg.text} />
                    </div>
                  ) : null}
                  {msg.attachments?.length ? (
                    <MessageAttachments
                      attachments={msg.attachments}
                      fetchDownloadUrl={fetchDownloadUrl}
                      variant="user"
                    />
                  ) : null}
                  <p className="mt-1.5 pl-1 text-[11px] font-medium text-slate-400">{msg.time}</p>
                </div>
              </div>
            );
          }
          const hideAgentText = msg.attachments?.length && (msg.text || "").trim() === "[Attachment]";
          return (
            <div key={msg.id} className="flex items-end justify-end gap-2.5">
              <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                {!hideAgentText ? (
                  <div className="chat-message-bubble rounded-2xl rounded-br-md bg-teal-700 px-4 py-3 text-sm leading-relaxed text-white shadow-md [&_.chat-md-p]:text-white [&_.chat-md-list]:text-white [&_strong]:text-white [&_a.chat-md-link]:text-blue-300 [&_a.chat-md-link]:underline [&_a.chat-md-link]:underline-offset-2 [&_a.chat-md-link:hover]:text-blue-200">
                    <ChatMessageBody text={msg.text} />
                  </div>
                ) : null}
                {msg.attachments?.length ? (
                  <MessageAttachments
                    attachments={msg.attachments}
                    fetchDownloadUrl={fetchDownloadUrl}
                    variant="agent"
                  />
                ) : null}
                <p className="mt-1.5 pr-1 text-right text-[11px] font-medium text-slate-400">{msg.time}</p>
              </div>
              <AgentMessageAvatar />
            </div>
          );
        })}
        <div ref={bottomRef} />
        {showNewMessageChip ? (
          <button
            type="button"
            className="sticky bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white shadow-md"
            onClick={() => {
              const ctrl = scrollControllerRef.current;
              ctrl?.dismissChip();
              setShowNewMessageChip(false);
              bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            New message
          </button>
        ) : null}
      </div>

      <footer className="ticketing-thread-footer">
        <ChatAttachmentComposer
          disabled={sendBusy || ticketSubmitBusy || !conversationId}
          controller={attachController}
          onFilesSelected={(files) => {
            for (const file of Array.from(files || [])) {
              const v = attachController.validateFile(file);
              if (!v.ok) {
                surfaceInboxHttpError(new Error(v.error));
                continue;
              }
              attachController.addLocalFile(file);
            }
          }}
        />
        <div className="relative rounded-2xl border border-slate-200/80 bg-slate-50/50 p-3">
          {showFromPicker ? (
            <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-slate-200/60 pb-3">
              <label htmlFor="ticket-from-sender" className="text-xs font-semibold text-slate-600">
                From
              </label>
              <select
                id="ticket-from-sender"
                value={selectedFromEmail}
                onChange={(e) => setSelectedFromEmail(e.target.value)}
                className="ui-input-subtle min-w-[220px] flex-1 text-xs"
                disabled={sendBusy || ticketSubmitBusy}
              >
                {emailSenders.map((sender) => (
                  <option key={sender.email} value={sender.email}>
                    {formatSenderLabel(sender)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <textarea
            ref={textareaRef}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              if (e.isComposing || e.nativeEvent?.isComposing) return;
              e.preventDefault();
              void handleSend();
            }}
            placeholder="Draft your reply... Enter to send, Shift+Enter newline."
            rows={3}
            className="w-full resize-none bg-transparent text-sm leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none"
            aria-label="Reply message"
          />
          <div className="mt-3 flex items-center justify-between border-t border-slate-200/60 pt-3">
            <div className="flex items-center gap-0.5">
              <ComposerLinkControl
                disabled={sendBusy || ticketSubmitBusy}
                textareaRef={textareaRef}
                value={replyText}
                onChange={setReplyText}
              />
              <span className="hidden text-[11px] font-medium text-slate-400 sm:inline">
                Enter to send · Shift+Enter newline
              </span>
            </div>
            {selectedTicket && submitTicketWithStatus ? (
              <div ref={sendActionsRef} className="relative flex items-center">
                <div className="inline-flex overflow-hidden rounded-xl ring-1 ring-teal-600/60 shadow-sm">
                  <button
                    type="button"
                    onClick={() => void handleSend()}
                    disabled={!attachController.canSend(replyText) || sendBusy || ticketSubmitBusy}
                    className="inline-flex h-[36px] items-center justify-center bg-teal-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-40"
                  >
                    Send only
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendActionsOpen((v) => !v)}
                    disabled={sendBusy || ticketSubmitBusy}
                    aria-label="Toggle ticket response options"
                    aria-expanded={sendActionsOpen}
                    className="inline-flex h-[36px] items-center justify-center border-l border-teal-500/70 bg-teal-600 px-2 text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${sendActionsOpen ? "rotate-180" : ""}`} aria-hidden />
                  </button>
                </div>
                {sendActionsOpen && (
                  <div className="absolute bottom-full right-0 z-30 mb-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                    <button
                      type="button"
                      disabled={ticketSubmitBusy}
                      onClick={() => void handleSubmitTicketAs("pending")}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-50 disabled:opacity-45"
                    >
                      Submit as pending
                    </button>
                    <button
                      type="button"
                      disabled={ticketSubmitBusy}
                      onClick={() => void handleSubmitTicketAs("on_hold")}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold text-sky-900 transition-colors hover:bg-sky-50 disabled:opacity-45"
                    >
                      Submit as on hold
                    </button>
                    <button
                      type="button"
                      disabled={ticketSubmitBusy}
                      onClick={() => void handleSubmitTicketAs("resolved")}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold text-emerald-900 transition-colors hover:bg-emerald-50 disabled:opacity-45"
                    >
                      Submit as resolved
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={!attachController.canSend(replyText) || sendBusy || ticketSubmitBusy}
                className="ui-btn-primary !py-2 !text-xs disabled:opacity-40"
              >
                Send only
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
